import React from 'react';

import UserCareManagerIcon from '@hero/ui-kit/icons/utility/UserCareManagerIcon';
import ClickableOpacity from '@hero/ui-kit/inputs/ClickableOpacity';
import { useUserStatusContext } from '../../../../context/UserStatus';
import { CareManager } from '../../types';
import CareManagerBadge from '../CareManagerBadge';
import * as BadgeStyle from '../CareManagerBadge/style.module.scss';
import * as Style from './style.module.scss';

interface CareManagerDropdownProps {
    careManagers: CareManager[] | undefined;
    isLoading?: boolean;
    onSelect: (careManager: CareManager) => void;
    selectedIds?: string[];
    disabled?: boolean;
    onSelectNa?: () => void;
    isNaSelected?: boolean;
}

const CareManagerDropdown: React.FC<CareManagerDropdownProps> = ({
    careManagers,
    isLoading = false,
    onSelect,
    selectedIds,
    disabled = false,
    onSelectNa,
    isNaSelected = false
}) => {
    const { userStatus } = useUserStatusContext();
    const hasSelection = selectedIds !== undefined;

    const renderItem = (cm: CareManager) => {
        const isActive = hasSelection && selectedIds.includes(String(cm.id));
        return (
            <ClickableOpacity
                key={cm.id}
                fullWidth
                alt={`${hasSelection ? 'Filter by' : 'Assign'} ${cm.first_name} ${cm.last_name}`}
                className={Style.item}
                onClick={() => onSelect(cm)}
                disabled={disabled}
            >
                <CareManagerBadge careManager={cm} active={isActive} />
            </ClickableOpacity>
        );
    };

    // When the logged-in user is themselves in the care-manager roster, surface their
    // own entry at the top of the list, separated from the rest by a divider. Roster
    // membership is the exact test — `userStatus.role` reports the PRIMARY org's role,
    // which is wrong once the user switches to another org.
    const currentCareManager = careManagers?.find(
        (cm) => cm.hero_user_id === userStatus?.hero_user_id
    );
    const restCareManagers = currentCareManager
        ? careManagers?.filter((cm) => cm.id !== currentCareManager.id)
        : careManagers;

    return (
        <div className={Style.list}>
            {isLoading && <p className="p-small">Loading...</p>}
            {currentCareManager && (
                <>
                    {renderItem(currentCareManager)}
                    <div className={Style.divider} />
                </>
            )}
            {restCareManagers?.map(renderItem)}
            {onSelectNa && (
                <>
                    <ClickableOpacity
                        fullWidth
                        alt="Filter by N/A"
                        className={Style.item}
                        onClick={onSelectNa}
                    >
                        <div className={BadgeStyle.wrapper}>
                            <div
                                className={`${BadgeStyle.avatarWrapper} ${isNaSelected ? BadgeStyle.avatarWrapperActive : ''}`}
                            >
                                <div className={BadgeStyle.avatarPlaceholder}>
                                    <UserCareManagerIcon size="small" />
                                </div>
                                {isNaSelected && (
                                    <div className={BadgeStyle.checkmark}>
                                        <svg viewBox="0 0 16 13" width="10" height="10">
                                            <path
                                                d="M1.1 7.1l1.7 1.7M5.1 11.3l9.5-9.5"
                                                stroke="#fff"
                                                strokeWidth="2.5"
                                                strokeLinecap="round"
                                            />
                                        </svg>
                                    </div>
                                )}
                            </div>
                            <p>N/A</p>
                        </div>
                    </ClickableOpacity>
                </>
            )}
        </div>
    );
};

export default CareManagerDropdown;
